// ============================================================
// CfaPanel.tsx — the case's CFA (Stage 4). Layout = the chosen mockup
// (settlement option A): a SUMMARY CARD — chips for the terms that apply,
// the risk-assessment line and "Edit terms" — and an EDIT-TERMS MODAL in the
// newer modal style (ModalFrame), grouped under plain headings:
//   Success fee · What can be deducted · Firm's own limit ·
//   Children & protected parties
// with one plain-English line per setting and the legal reference behind ⓘ.
//
// (Batch 2) READ-FIRST: the card is the read view; "Edit terms" (the pencil)
// opens the modal — the CFA's editor. No CFA → a clear "Not set up yet" state
// (no chips pretending terms exist) with "Create from firm defaults". A
// migrated CFA → ONE review box (ActionBox): [Check terms] opens the editor,
// [Mark as checked] needs the tick "I’ve compared these terms with the signed
// CFA"; who + when once recorded. The preview / statement only point here.
// `locked` (a prepared / approved statement uses these terms): read-only.
// After a save / check / create: emitCaseChange(caseId, 'cfa').
//
// Behaviour kept from the old card:
//   • "Create from firm defaults" when there's none.
//   • EXPLICIT Save (PATCH /settlement/cfa, audited server-side) + Discard.
//   • Unsaved-changes guard: leaving the page asks (UnsavedGuard), and so
//     does CLOSING THE MODAL with unsaved changes (✕ / Esc / backdrop /
//     Cancel → "Discard your changes?").
//   • The draft is lifted to the section, so the preview shows unsaved terms.
//   • A 409 (someone else saved) → banner with Reload (re-reads the CFA).
//   • Validation: bad % text (stages / caps) blocks Save and counts as unsaved.
//   • MIGRATED CFA → the review box on the summary card (CFA_REVIEW_BUTTON_ID
//     = its "Check terms" button; the preview's one line links to it).
//   • "Signed on" checks (cfaDates.ts): future = blocked; before the accident
//     date / pre-April 2013 = amber.
// ============================================================
import React, { useCallback, useEffect, useId, useState } from 'react'
import { createCfaApi, reviewCfaApi, updateCfaApi } from '../../api/casevoApi'
import { emitCaseChange } from '../../utils/caseChangeEvents'
import ActionBox from '../shared/ActionBox'
import { UnsavedGuard } from '../../hooks/useUnsavedChangesGuard'
import DateInput from '../shared/DateInput'
import ModalFrame, { HERO_PILL_SOFT } from '../shared/ModalFrame'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import { CARD, B_PRIMARY, B_SECONDARY, B_SECONDARY_SM, LABEL, NOTE, INPUT, BIcon, errorText, fmtDate } from '../billing/billingUi'
import { SettingsSaved, SettingsSwitch, SIcon } from '../settings/settingsUi'
import { DeductsLess, ReduceOrderEditor, stageDraftOf, stagesOfDraft, type StageDraft } from '../admin/PiSettlementDefaultsPanel'
import { Banner, InfoTip, PercentField, SuccessFeeBasisField } from './settlementUi'
import { cfaDateWarnings, futureDateError } from './cfaDates'
import { ukDate, textDateToIso } from '../../utils/civilDate'
import {
  CAP_BASE_LABEL, CFA_STAGES, CFA_STAGE_LABEL, percentTextToBp,
  type CfaEditable, type CfaRecord, type CfaStage, type DeductionCapBase,
} from '../../types/settlement.types'

// The review box's "Check terms" button — the preview's one line ("Go to the
// CFA") and the statement's "cfa" fix focus it.
export const CFA_REVIEW_BUTTON_ID = 'cfa-check-terms'
// The card itself (scroll target; the "Edit terms" button inside it).
export const CFA_CARD_ID = 'settlement-cfa'
export const CFA_EDIT_BUTTON_ID = 'cfa-edit-terms'
export const CFA_CREATE_LABEL = 'Create from firm defaults'

export const cfaEditableOf = (c: CfaRecord): CfaEditable => ({
  signedOn: c.signedOn,
  successFeeOnRecoveredCostsOnly: c.successFeeOnRecoveredCostsOnly,
  capIncludesVat: c.capIncludesVat,
  capBaseIncludesInterest: c.capBaseIncludesInterest,
  counselInCap: c.counselInCap,
  shortfallClause: c.shortfallClause,
  s74_3Clause: c.s74_3Clause,
  ateDeductible: c.ateDeductible,
  noDeductionsFromChildren: c.noDeductionsFromChildren,
  totalCapBasisPoints: c.totalCapBasisPoints,
  totalCapBase: c.totalCapBase,
  totalCapReduceOrder: c.totalCapReduceOrder,
  riskAssessmentNote: c.riskAssessmentNote,
  stages: c.stages,
  outsetDeductionEstimateBasisPoints: c.outsetDeductionEstimateBasisPoints,
  wordingVersion: c.wordingVersion,
})
// The keys that differ between the saved CFA and the draft.
export const cfaChanges = (saved: CfaEditable, draft: CfaEditable): Partial<CfaEditable> => {
  const out: Record<string, unknown> = {}
  for (const k of Object.keys(draft) as (keyof CfaEditable)[]) {
    if (JSON.stringify(saved[k]) !== JSON.stringify(draft[k])) out[k] = draft[k]
  }
  return out as Partial<CfaEditable>
}

// ── The terms, in plain English (legal reference behind ⓘ) ──
type BoolKey = 'successFeeOnRecoveredCostsOnly' | 'capIncludesVat' | 'capBaseIncludesInterest' | 'counselInCap' | 'shortfallClause' | 's74_3Clause' | 'ateDeductible' | 'noDeductionsFromChildren'
interface TermLine { key: BoolKey; title: string; line: string; legal?: string; lessIs: boolean }
export const TERM_LINES: Record<BoolKey, TermLine> = {
  successFeeOnRecoveredCostsOnly: { key: 'successFeeOnRecoveredCostsOnly', title: 'Only on costs the other side pays', line: 'Work the success fee out on the costs actually recovered, never more than the firm’s own.', lessIs: true },
  capIncludesVat: { key: 'capIncludesVat', title: 'VAT counts inside the 25% cap', line: 'The cap on the success fee includes its VAT.', lessIs: true,
    legal: 'Conditional Fee Agreements Order 2013, art. 5: the success fee in a personal-injury claim is capped at 25% of general damages and past losses (after CRU), or 100% on appeal. With this on, the 25% includes VAT, so less is deducted.' },
  capBaseIncludesInterest: { key: 'capBaseIncludesInterest', title: 'Interest counts towards the cap', line: 'Interest on damages adds to the figure the cap is worked out from.', lessIs: false,
    legal: 'CFA Order 2013, art. 5: the cap is a share of general damages and past pecuniary loss. Whether interest counts depends on the CFA wording.' },
  counselInCap: { key: 'counselInCap', title: 'Pass on counsel’s success fee', line: 'Counsel’s uplift is charged to the client, inside the same cap.', lessIs: false,
    legal: 'LASPO 2012 s.44 and CFA Order 2013 art. 5: every success fee charged to the client counts towards the one cap.' },
  shortfallClause: { key: 'shortfallClause', title: 'Charge costs not recovered', line: 'The gap between the firm’s costs and what the other side paid.', lessIs: false,
    legal: 'A “shortfall clause” in the CFA lets the firm take its unrecovered base costs from damages. It sits outside the 25% success-fee cap.' },
  s74_3Clause: { key: 's74_3Clause', title: 'Allow that gap in County Court claims', line: 'Needs a signed s.74(3) agreement in the CFA.', lessIs: false,
    legal: 'Solicitors Act 1974 s.74(3): in County Court proceedings the firm can’t charge more than the court allows unless the client agreed to it in writing.' },
  ateDeductible: { key: 'ateDeductible', title: 'Deduct the ATE insurance premium', line: 'Only the part the other side didn’t pay.', lessIs: false,
    legal: 'LASPO 2012 s.46: an after-the-event insurance premium usually can’t be recovered from the other side in a personal-injury claim, so the CFA may let the firm take it from damages.' },
  noDeductionsFromChildren: { key: 'noDeductionsFromChildren', title: 'Never deduct from their damages', line: 'On: nothing is taken; the firm absorbs it.', lessIs: true,
    legal: 'CPR 21.12: any deduction from a child’s or protected party’s damages needs the court’s approval, and is limited to 25% of damages (CPR 21.12(7)–(8)).' },
}
const DEDUCTED: BoolKey[] = ['capIncludesVat', 'capBaseIncludesInterest', 'counselInCap', 'shortfallClause', 's74_3Clause', 'ateDeductible']

const CAP_BASE_SHORT: Record<DeductionCapBase, string> = {
  DAMAGES_TOTAL: 'damages', DAMAGES_NET_OF_CRU: 'damages less CRU', PSLA_AND_PAST_LOSS_NET_OF_CRU: 'PSLA + past loss',
}
const STAGE_SHORT: Record<CfaStage, string> = { PRE_ISSUE: 'Before issue', POST_ISSUE: 'After issue', LISTED_FOR_TRIAL: 'Listed', TRIAL: 'At trial', APPEAL: 'Appeal' }

// The rate that applies at `stage`: its own row, else the nearest earlier one.
const rateAt = (stages: CfaEditable['stages'], stage: CfaStage): { bp: number; from: CfaStage } | null => {
  for (let i = CFA_STAGES.indexOf(stage); i >= 0; i--) {
    const hit = stages.find((s) => s.stage === CFA_STAGES[i])
    if (hit) return { bp: hit.rateBasisPoints, from: hit.stage }
  }
  return null
}
const pct = (bp: number): string => `${bp / 100}%`

// The summary chips — only the terms that apply.
export const cfaChips = (c: CfaEditable, stageReached: CfaStage): { text: string; neutral?: boolean }[] => {
  const out: { text: string; neutral?: boolean }[] = []
  const r = rateAt(c.stages, stageReached)
  out.push(r ? { text: `${pct(r.bp)} success fee · ${CFA_STAGE_LABEL[stageReached].toLowerCase()}` } : { text: 'No success fee at this stage', neutral: true })
  out.push({ text: c.successFeeOnRecoveredCostsOnly ? 'On recovered costs only' : 'On all base costs' })
  if (c.capIncludesVat) out.push({ text: '25% cap includes VAT' })
  if (c.capBaseIncludesInterest) out.push({ text: 'Interest counts in the cap' })
  if (c.counselInCap) out.push({ text: 'Counsel’s fee passed on' })
  if (c.shortfallClause) out.push({ text: 'Shortfall clause' })
  if (c.shortfallClause && c.s74_3Clause) out.push({ text: 's.74(3) agreement' })
  if (c.ateDeductible) out.push({ text: 'ATE premium deducted' })
  if (c.totalCapBasisPoints != null) out.push({ text: `Firm cap ${pct(c.totalCapBasisPoints)} of ${CAP_BASE_SHORT[c.totalCapBase]}` })
  if (c.noDeductionsFromChildren) out.push({ text: 'No deductions from children', neutral: true })
  return out
}

interface Props {
  caseId: string
  cfa: CfaRecord | null
  draft: CfaEditable | null
  setDraft: (d: CfaEditable) => void
  // Stage text boxes (so a half-typed rate isn't lost) — lifted with the draft.
  stageText: StageDraft
  setStageText: (d: StageDraft) => void
  onChanged: () => Promise<void>
  // The case's accident date (YYYY-MM-DD / null) and today (Europe/London),
  // for the Signed on checks.
  accidentDate: string | null
  today: string
  // The stage the claim has reached (Court & proceedings) — picks the chip's rate.
  stageReached?: CfaStage
  // (Batch 2) A prepared / approved statement uses these terms: read-only.
  locked?: boolean
  // (Batch 2) "Create from firm defaults", shared with the preview's box and
  // the statement's fix (the section owns it); absent → this card does it.
  create?: { run: () => void; busy: boolean; error: string | null }
  // (Batch 2) Bumped by the section to open the editor (statement "cfa" fix).
  openRequest?: number
  // (Batch 2) After a save / check / create — default: emitCaseChange 'cfa'.
  onSaved?: () => void
}

const CHIP = 'inline-flex items-center text-[12.5px] px-[10px] py-[3px] rounded-full whitespace-nowrap'

const CfaPanel: React.FC<Props> = ({ caseId, cfa, draft, setDraft, stageText, setStageText, onChanged, accidentDate, today, stageReached = 'PRE_ISSUE', locked, create, openRequest, onSaved }) => {
  const notify = (): void => { if (onSaved) onSaved(); else emitCaseChange(caseId, 'cfa') }
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [conflict, setConflict] = useState(false)
  const [open, setOpen] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)
  const [reviewError, setReviewError] = useState<string | null>(null)
  // "I’ve compared these terms with the signed CFA" — required for Mark as checked.
  const [compared, setCompared] = useState(false)
  // Percentage boxes holding invalid text (they don't reach the draft).
  const [badPct, setBadPct] = useState<Record<string, boolean>>({})
  // The Signed on box holds a half-typed / invalid date (it doesn't reach the draft).
  const [signedTyping, setSignedTyping] = useState(false)
  const markBad = useCallback((k: string) => (b: boolean) => setBadPct((x) => (x[k] === b ? x : { ...x, [k]: b })), [])
  const noteId = useId()
  // The section asks for the editor (a statement's "cfa" fix).
  useEffect(() => { if (openRequest && cfa && !locked) setOpen(true) }, [openRequest]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!cfa || !draft) {
    const runCreate = create?.run ?? ((): void => {
      setBusy(true); setError(null)
      createCfaApi(caseId).then(onChanged).then(notify).catch((e) => setError(errorText(e, 'Couldn’t create the CFA.'))).finally(() => setBusy(false))
    })
    const creating = create ? create.busy : busy
    const createError = create ? create.error : error
    return (
      <section id={CFA_CARD_ID} className={CARD} aria-label="CFA">
        <CfaHead sub={<span className="font-semibold text-casevo-warn">Not set up yet</span>} />
        <div className="px-5 pb-5">
          <p className={`${NOTE} m-0`}>There’s no CFA on this case yet, so nothing can be worked out. Create one from the firm’s standard terms (Admin › PI settlements), then check it against what the client signed.</p>
          {createError && <p className="mt-2 text-[12.5px] text-casevo-danger" role="alert">{createError}</p>}
          <button type="button" className={`${B_PRIMARY} mt-3`} disabled={creating} onClick={runCreate}>{creating ? 'Creating…' : CFA_CREATE_LABEL}</button>
        </div>
      </section>
    )
  }

  const savedTerms = cfaEditableOf(cfa)
  const stagesParsed = stagesOfDraft(stageText)
  const changes = cfaChanges(savedTerms, draft)
  const stagesBad = stagesParsed === null
  // Boxes whose text doesn't reach the draft: a bad % or a half-typed Signed on date.
  const pctBad = Object.values(badPct).some(Boolean) || signedTyping
  const dirty = Object.keys(changes).length > 0 || stagesBad || pctBad
  const set = <K extends keyof CfaEditable>(k: K, v: CfaEditable[K]): void => setDraft({ ...draft, [k]: v })
  // A future signed date is BLOCKED (server 400; Save disabled here); the
  // other date checks stay amber warnings.
  const signedFuture = futureDateError('The signed date', draft.signedOn, today)
  const dateWarnings = cfaDateWarnings(draft.signedOn, accidentDate, today).filter((w) => !w.startsWith('Signed on a date in the future'))
  const needsReview = cfa.source === 'MIGRATED' && cfa.needsReview && !cfa.reviewedAt

  const save = async (): Promise<void> => {
    if (!dirty || stagesBad || pctBad) return
    setBusy(true); setError(null); setConflict(false)
    try {
      await updateCfaApi(caseId, changes, cfa.updatedAt)
      await onChanged()
      notify()
      setOpen(false)
      setSaved(true); window.setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      const status = (e as { response?: { status?: number } }).response?.status
      if (status === 409) setConflict(true)
      else setError(errorText(e, 'Couldn’t save the CFA.'))
    } finally { setBusy(false) }
  }
  // Reload = the latest saved CFA (their changes), dropping this draft.
  const reloadLatest = async (): Promise<void> => {
    setBusy(true)
    try { await onChanged(); setConflict(false); setError(null); setBadPct({}) } catch (e) { setError(errorText(e, 'Couldn’t reload the CFA.')) } finally { setBusy(false) }
  }
  const discard = (): void => {
    if (conflict) { void reloadLatest(); return }
    setDraft(savedTerms); setStageText(stageDraftOf(savedTerms.stages)); setError(null); setBadPct({})
  }
  // ✕ / Esc / backdrop / Cancel: with unsaved changes, ask first.
  const requestClose = (): void => {
    if (busy) return
    if (dirty) { setConfirmClose(true); return }
    setOpen(false)
  }
  const signed = textDateToIso(cfa.signedOn ?? '')
  const chips = cfaChips(savedTerms, stageReached)

  const boolRow = (k: BoolKey): React.ReactNode => {
    const t = TERM_LINES[k]
    const on = draft[k] as boolean
    return (
      <div key={k} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-[14px] py-[10px] border-t border-casevo-border-soft first:border-t-0">
        <div className="min-w-0">
          <div className="text-[13.5px] text-casevo-forest font-medium inline-flex flex-wrap items-center gap-[6px]">
            {t.title}
            {t.legal && <InfoTip label={`The legal bit: ${t.title}`}>{t.legal}</InfoTip>}
            {on === t.lessIs && <DeductsLess />}
          </div>
          <div className="text-[12.5px] text-casevo-text-soft mt-[2px] leading-[1.45]">{t.line}</div>
        </div>
        <SettingsSwitch checked={on} onChange={() => set(k, !on as never)} label={t.title} disabled={busy} />
      </div>
    )
  }

  return (
    <section id={CFA_CARD_ID} className={CARD} aria-label="CFA">
      <CfaHead
        sub={`${signed ? `Signed ${ukDate(signed)}` : 'No signed date'}${savedTerms.wordingVersion ? ` · ${savedTerms.wordingVersion}` : ''}${cfa.source === 'MIGRATED' ? ' · migrated' : ''}`}
        right={<>
          {saved && <SettingsSaved />}
          {!locked && <button type="button" id={CFA_EDIT_BUTTON_ID} className={B_SECONDARY_SM} onClick={() => setOpen(true)}><BIcon name="pen" size={13} />Edit terms</button>}
        </>} />
      <div className="px-5 pb-4">
        <ul className="m-0 p-0 list-none flex flex-wrap gap-[8px]" aria-label="CFA terms">
          {chips.map((c) => (
            <li key={c.text} className={`${CHIP} ${c.neutral ? 'border border-casevo-border text-casevo-text-soft' : 'bg-casevo-mint text-casevo-brand dark:text-casevo-link'}`}>{c.text}</li>
          ))}
        </ul>
        <p className={`${NOTE} mt-[10px] mb-0 truncate`} title={savedTerms.riskAssessmentNote || undefined}>
          Risk assessment: {savedTerms.riskAssessmentNote.trim() ? <span className="text-casevo-forest">{savedTerms.riskAssessmentNote}</span> : <span>none recorded</span>}
        </p>
        {/* (Batch 2) The ONE place the page asks for the CFA check. */}
        {needsReview && (
          <ActionBox className="mt-3" ariaLabel="Check the CFA terms" title="Check the CFA terms against the signed agreement"
            message={<>
              They were copied from the case’s old Funding setting (‘{cfa.legacyLabel ?? 'not recorded'}’), so they may not match what the client signed. Correct anything that differs, then mark them as checked.
            </>}
            // (Audit fix batch 2) The tick sits in a plain white section, not on a tint.
            form={
              <label className="inline-flex items-center gap-[8px] text-[13px] text-casevo-forest cursor-pointer select-none">
                <input type="checkbox" className="h-[16px] w-[16px] accent-casevo-brand shrink-0" checked={compared} disabled={busy || locked} onChange={(e) => setCompared(e.target.checked)} />
                I’ve compared these terms with the signed CFA
              </label>
            }
            actions={[
              ...(locked ? [] : [{ id: CFA_REVIEW_BUTTON_ID, label: 'Check terms', kind: 'link' as const, arrow: true, onClick: () => setOpen(true) }]),
              {
                label: 'Mark as checked', kind: 'primary' as const, busy, busyLabel: 'Saving…',
                // (Low 14) Locked by a statement: say THAT, not "tick the box".
                disabled: locked || !compared,
                disabledReason: locked ? 'The figures are locked by the settlement statement — send it back for changes first.' : 'Tick the box first.',
                onClick: () => {
                  setBusy(true); setReviewError(null)
                  reviewCfaApi(caseId).then(onChanged).then(notify)
                    .catch((e) => setReviewError(errorText(e, 'Couldn’t record the check.'))).finally(() => setBusy(false))
                },
              },
            ]} />
        )}
        {reviewError && <p className="mt-2 text-[12.5px] text-casevo-danger" role="alert">{reviewError}</p>}
        {cfa.reviewedAt && <p className={`${NOTE} mt-2 mb-0`}>Terms checked against the signed CFA by {cfa.reviewedBy ?? 'a former member'} on {fmtDate(cfa.reviewedAt, 'long')}.</p>}
      </div>

      <UnsavedGuard dirty={dirty && !busy} />

      <ModalFrame isOpen={open} onClose={requestClose} size="xl" scrollBody dismissible={!busy}
        eyebrow="CFA terms" pills={<span className={HERO_PILL_SOFT}>{signed ? `Signed ${ukDate(signed)}` : 'Not signed'}</span>}
        title="Edit the CFA terms"
        subtitle="These decide what can be taken from the client’s damages. Settings marked “deducts less” leave the client with more."
        footerLeft={<>
          <button type="button" className={B_SECONDARY} disabled={busy} onClick={requestClose}>Cancel</button>
          {dirty ? <span className="text-[12.5px] font-semibold text-casevo-warn">Unsaved changes · the preview already shows them</span> : <span className={NOTE}>Changes apply to this CFA only.</span>}
        </>}
        footerRight={<button type="button" className={B_PRIMARY} disabled={!dirty || stagesBad || pctBad || conflict || busy || !!signedFuture} onClick={() => { void save() }}>{busy ? 'Saving…' : 'Save CFA'}</button>}>
        {conflict && (
          <div className="mb-4">
            <Banner tone="error" action={<button type="button" className={B_SECONDARY_SM} disabled={busy} onClick={() => { void reloadLatest() }}>Reload</button>}>
              Someone else saved this CFA since you opened it, so your changes weren’t saved. Reload to see theirs, then make your edit again.
            </Banner>
          </div>
        )}
        {error && <div className="mb-4"><Banner tone="error">{error}</Banner></div>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-[40px] gap-y-[22px]">
          {/* ── LEFT: the agreement, success fee, firm's own limit ── */}
          <div className="min-w-0 flex flex-col gap-[22px]">
            <div>
              <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-[14px]">
                <div className="min-w-0">
                  <span className={LABEL}>Signed on</span>
                  <DateInput value={draft.signedOn} onChange={(v) => set('signedOn', v)} ariaLabel="CFA signed date" onValidityChange={(ok) => setSignedTyping(!ok)} />
                </div>
                <div className="min-w-0">
                  <label className={LABEL} htmlFor={`${noteId}-wv`}>CFA wording version</label>
                  <input id={`${noteId}-wv`} className={INPUT} value={draft.wordingVersion} maxLength={100} placeholder="e.g. CFA v3 (2024)" disabled={busy} onChange={(e) => set('wordingVersion', e.target.value)} />
                </div>
              </div>
              {signedFuture && <p className="text-[12.5px] text-casevo-danger mt-[6px] mb-0" role="alert">{signedFuture}</p>}
              {dateWarnings.length > 0 && <div className="mt-[8px] flex flex-col gap-[6px]">{dateWarnings.map((w) => <Banner key={w} tone="warn">{w}</Banner>)}</div>}
            </div>

            <div>
              <GroupHead title="Success fee" sub="The uplift on the firm’s costs if the claim wins. A stage left blank uses the rate before it." />
              <StageBoxes text={stageText} disabled={busy} idPrefix={`cfa-${cfa.id}`} onChange={(d) => {
                setStageText(d)
                const st = stagesOfDraft(d)
                if (st) set('stages', st)
              }} />
              {stagesBad && <p className="text-[12px] text-casevo-danger mt-[5px] mb-0" role="alert">Each rate must be a percentage from 0 to 100.</p>}
              <SuccessFeeBasisField name={`cfa-basis-${cfa.id}`} recovered={draft.successFeeOnRecoveredCostsOnly} disabled={busy} onChange={(v) => set('successFeeOnRecoveredCostsOnly', v)} />
            </div>

            <div>
              <GroupHead title="Firm’s own limit" sub="A promise to the client: total deductions never go above this." />
              <div className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-[14px] items-start">
                <PercentField label="Limit" bp={draft.totalCapBasisPoints} onBp={(v) => set('totalCapBasisPoints', v)} placeholder="None" disabled={busy} onBadChange={markBad('totalCap')} />
                <div className="min-w-0">
                  <label className={LABEL} htmlFor={`cfa-base-${cfa.id}`}>Percentage of</label>
                  <select id={`cfa-base-${cfa.id}`} className={INPUT} value={draft.totalCapBase} disabled={busy} onChange={(e) => set('totalCapBase', e.target.value as DeductionCapBase)}>
                    {(Object.keys(CAP_BASE_LABEL) as DeductionCapBase[]).map((b) => <option key={b} value={b}>{CAP_BASE_LABEL[b]}{b === 'PSLA_AND_PAST_LOSS_NET_OF_CRU' ? ' (deducts less)' : ''}</option>)}
                  </select>
                </div>
              </div>
              <p className={`${NOTE} mt-[6px] mb-[8px]`}>Blank = no firm limit. If the limit is reached, reduce in this order:</p>
              <ReduceOrderEditor order={draft.totalCapReduceOrder} onChange={(o) => set('totalCapReduceOrder', o)} disabled={busy} />
            </div>

            <div>
              <GroupHead title="Risk assessment" sub="Why this success rate is justified. A 100% rate, or a fee exactly at the cap, with no note shows a warning." />
              <label htmlFor={noteId} className="sr-only">Risk assessment note</label>
              <textarea id={noteId} className={`${INPUT} h-auto min-h-[76px] py-2 leading-[1.45]`} value={draft.riskAssessmentNote} maxLength={5000} disabled={busy}
                onChange={(e) => set('riskAssessmentNote', e.target.value)} placeholder="e.g. Liability disputed; 50/50 on causation…" />
              <div className="mt-[10px]">
                <PercentField label="Deduction estimate given at the outset" bp={draft.outsetDeductionEstimateBasisPoints} onBp={(v) => set('outsetDeductionEstimateBasisPoints', v)}
                  hint="The % of damages the client was told to expect." disabled={busy} onBadChange={markBad('outset')} />
              </div>
            </div>
          </div>

          {/* ── RIGHT: what can be deducted; children & protected parties ── */}
          <div className="min-w-0 flex flex-col gap-[22px]">
            <div>
              <GroupHead title="What can be deducted" sub="Each switch is one term of the agreement the client signed." />
              <div>{DEDUCTED.map(boolRow)}</div>
            </div>
            <div>
              <GroupHead title="Children & protected parties" sub="The court must approve any deduction from their damages." />
              <div>{boolRow('noDeductionsFromChildren')}</div>
            </div>
          </div>
        </div>
      </ModalFrame>

      <ConfirmDialogue isOpen={confirmClose} title="Discard your changes?" message="Your changes to the CFA terms haven’t been saved."
        confirmLabel="Discard changes" cancelLabel="Keep editing"
        onCancel={() => setConfirmClose(false)}
        onConfirm={() => { setConfirmClose(false); discard(); setOpen(false) }} />
    </section>
  )
}

const CfaHead: React.FC<{ sub: React.ReactNode; right?: React.ReactNode }> = ({ sub, right }) => (
  <div className="flex items-start gap-3 px-5 pt-4 pb-3 flex-wrap">
    <div className="w-[34px] h-[34px] rounded-[10px] bg-casevo-mint text-casevo-brand dark:text-casevo-link grid place-items-center shrink-0"><SIcon name="file" size={16} /></div>
    <div className="flex-1 min-w-[160px]">
      <h3 className="m-0 text-[15.5px] font-semibold text-casevo-forest">Conditional fee agreement</h3>
      <p className="m-0 mt-[2px] text-[12.5px] text-casevo-text-soft">{sub}</p>
    </div>
    {right && <div className="flex items-center gap-2 shrink-0">{right}</div>}
  </div>
)

const GroupHead: React.FC<{ title: string; sub?: string }> = ({ title, sub }) => (
  <div className="mb-[8px]">
    <h4 className="m-0 text-[14.5px] font-semibold text-casevo-forest">{title}</h4>
    {sub && <p className="m-0 mt-[2px] text-[12.5px] text-casevo-text-soft leading-[1.45]">{sub}</p>}
  </div>
)

// The five stage rates on ONE line: same-height labels (one line each, the
// full name in the accessible label), same-size boxes with a pinned %.
const StageBoxes: React.FC<{ text: StageDraft; onChange: (d: StageDraft) => void; disabled?: boolean; idPrefix: string }> = ({ text, onChange, disabled, idPrefix }) => (
  <div className="grid grid-cols-5 gap-[8px] items-end">
    {CFA_STAGES.map((s) => {
      const bad = percentTextToBp(text[s]) === undefined
      return (
        <div key={s} className="min-w-0">
          <label htmlFor={`${idPrefix}-${s}`} className="block text-[12px] font-semibold text-casevo-text-soft mb-[5px] truncate" title={CFA_STAGE_LABEL[s]}>{STAGE_SHORT[s]}</label>
          <span className="relative block">
            <input id={`${idPrefix}-${s}`} aria-label={`${CFA_STAGE_LABEL[s]} rate`} className={`${INPUT} pr-[24px] text-right tabular-nums ${bad ? '!border-casevo-danger' : ''}`}
              value={text[s]} inputMode="decimal" placeholder="—" autoComplete="off" disabled={disabled}
              aria-invalid={bad || undefined} onChange={(e) => onChange({ ...text, [s]: e.target.value })} />
            <span aria-hidden="true" className="absolute right-[9px] top-1/2 -translate-y-1/2 text-[13px] text-casevo-text-soft pointer-events-none">%</span>
          </span>
        </div>
      )
    })}
  </div>
)

export default CfaPanel
